import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Grid,
  TextField,
  InputAdornment,
  Tooltip,
  Autocomplete,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RefreshIcon from '@mui/icons-material/Refresh';
import StoreIcon from '@mui/icons-material/Store';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../../contexts/AuthContext';
import { BranchPriceService, BranchPriceItem } from './services/branchPriceService';
import type { Branch, InventoryItem } from '../inventory/types/inventory.types';
import SingleProductSelectionModal, { InventoryItemForSelection } from './components/SingleProductSelectionModal';
import CopyBranchPricesModal from './components/CopyBranchPricesModal';
import { BranchPriceModal } from '../inventory/components/BranchPriceModal';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// Fiyat güncelleme field tipi
type PriceFieldType = 'salePrice' | 'listPrice' | 'packagePrice';

// Ürün bilgisi ile zenginleştirilmiş BranchPrice
interface BranchPriceWithProduct extends BranchPriceItem {
  stockCode: string;
  productName: string;
  categoryName?: string;
  unit?: string;
  useBranchPricing?: boolean; // ✅ Şube fiyatlandırma aktif mi?
}



interface BranchPricesProps {
  isManager?: boolean;
  managerBranchId?: string;
}

const BranchPrices: React.FC<BranchPricesProps> = ({ isManager = false, managerBranchId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  // Auth context'ten veri al
  const { 
    userDetails,
    branches,
    categories,
    warehouses,
    getCategoryName
  } = useAuth();

  // State
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchPrices, setBranchPrices] = useState<BranchPriceWithProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // ✅ Fiyat Güncelleme Modal State'leri
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedPriceItem, setSelectedPriceItem] = useState<BranchPriceWithProduct | null>(null);
  const [selectedField, setSelectedField] = useState<PriceFieldType | null>(null);
  const [updateValue, setUpdateValue] = useState<number>(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const updateInputRef = useRef<HTMLInputElement>(null);
  
  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // ✅ YENİ: Şube Fiyatı Ekleme Modal State'leri
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] = useState(false);
  const [isBranchPriceModalOpen, setIsBranchPriceModalOpen] = useState(false);
  const [selectedProductForBranchPrice, setSelectedProductForBranchPrice] = useState<InventoryItem | null>(null);

  // ✅ YENİ: Şube Fiyatlarını Başka Şubeye Kopyalama Modalı
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  // CompanyId al
  useEffect(() => {
    if (userDetails?.companyId) {
      setCompanyId(userDetails.companyId);
    }
  }, [userDetails]);

  // Manager modunda şubeyi otomatik seç ve kilitle
  useEffect(() => {
    if (isManager && managerBranchId && branches.length > 0) {
      const managerBranch = branches.find(b => b.id === managerBranchId);
      if (managerBranch) {
        setSelectedBranch(managerBranch);
      }
    }
  }, [isManager, managerBranchId, branches]);

  // Şube seçildiğinde fiyatları yükle
  useEffect(() => {
    if (companyId && selectedBranch) {
      loadBranchPrices();
    } else {
      setBranchPrices([]);
    }
  }, [companyId, selectedBranch]);

  // Şube fiyatlarını yükle (API artık ürün bilgilerini de döndürüyor)
  const loadBranchPrices = async () => {
    if (!companyId || !selectedBranch) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Şube fiyatlarını getir (ürün bilgileri dahil)
      const prices = await BranchPriceService.getBranchPrices(companyId, selectedBranch.id);
      
      if (prices.length === 0) {
        setBranchPrices([]);
        setLoading(false);
        return;
      }
      
      // API'dan gelen verileri BranchPriceWithProduct formatına dönüştür
      const enrichedPrices: BranchPriceWithProduct[] = prices.map(price => ({
        ...price,
        stockCode: price.stockCode || '-',
        productName: price.productName || tt('Bilinmeyen Ürün', 'Unknown Product'),
        categoryName: price.categoryId ? getCategoryName(price.categoryId) : undefined,
        unit: price.unit || tt('Adet', 'Piece')
      }));
      
      setBranchPrices(enrichedPrices);
    } catch (err: any) {
      console.error('Şube fiyatları yüklenirken hata:', err);
      setError(tt('Şube fiyatları yüklenirken bir hata oluştu.', 'An error occurred while loading branch prices.'));
    } finally {
      setLoading(false);
    }
  };

  // Yenile
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadBranchPrices();
    setRefreshing(false);
  };

  // ✅ Fiyat hücresine tıklandığında modal aç
  const handlePriceClick = (item: BranchPriceWithProduct, field: PriceFieldType) => {
    setSelectedPriceItem(item);
    setSelectedField(field);
    
    // Mevcut değeri al
    let currentValue = 0;
    if (field === 'salePrice') currentValue = item.salePrice || 0;
    else if (field === 'listPrice') currentValue = item.listPrice || 0;
    else if (field === 'packagePrice') currentValue = item.packagePrice || 0;
    
    setUpdateValue(currentValue);
    setIsUpdateModalOpen(true);
    
    // Input'a focus
    setTimeout(() => {
      updateInputRef.current?.select();
    }, 100);
  };

  // ✅ Fiyat güncelleme
  const handleUpdatePrice = async () => {
    if (!selectedPriceItem || !selectedField || !companyId) return;
    
    setIsUpdating(true);
    
    try {
      // ✅ Kullanıcı bilgilerini hazırla (log için)
      const userId = userDetails?.id || null;
      const userName = userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() : null;
      
      // Güncelleme DTO'su oluştur
      const updateDto: {
        salePrice: number;
        saleCurrency?: string;
        listPrice?: number;
        listCurrency?: string;
        packagePrice?: number;
        userId?: string;
        userName?: string;
        branchId?: string;
        branchName?: string;
      } = {
        salePrice: selectedField === 'salePrice' ? updateValue : selectedPriceItem.salePrice,
        saleCurrency: selectedPriceItem.saleCurrency,
        listPrice: selectedField === 'listPrice' ? updateValue : selectedPriceItem.listPrice,
        listCurrency: selectedPriceItem.listCurrency,
        packagePrice: selectedField === 'packagePrice' ? updateValue : selectedPriceItem.packagePrice,
        // ✅ YENİ: Fiyat log için kullanıcı ve şube bilgileri
        userId: userId || undefined,
        userName: userName || undefined,
        branchId: selectedBranch?.id,
        branchName: selectedBranch?.name
      };
      
      await BranchPriceService.updateBranchPrice(companyId, selectedPriceItem.id, updateDto);
      
      // Başarı mesajı
      const fieldName = selectedField === 'salePrice' ? tt('Satış Fiyatı', 'Sale Price') 
        : selectedField === 'listPrice' ? tt('Liste Fiyatı', 'List Price') 
        : tt('Paket Fiyatı', 'Package Price');
      showSnackbar(`${fieldName} ${tt('başarıyla güncellendi!', 'updated successfully!')}`, 'success');
      
      // Listeyi yenile
      await loadBranchPrices();
      
      // Modalı kapat
      setIsUpdateModalOpen(false);
      setSelectedPriceItem(null);
      setSelectedField(null);
      
    } catch (err: any) {
      console.error('Fiyat güncellenirken hata:', err);
      showSnackbar(tt('Fiyat güncellenirken bir hata oluştu.', 'An error occurred while updating price.'), 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ Snackbar göster
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ✅ Modal kapat
  const handleCloseUpdateModal = () => {
    if (!isUpdating) {
      setIsUpdateModalOpen(false);
      setSelectedPriceItem(null);
      setSelectedField(null);
    }
  };

  // ✅ Field başlığı al
  const getFieldTitle = (field: PriceFieldType | null): string => {
    if (field === 'salePrice') return tt('Satış Fiyatı', 'Sale Price');
    if (field === 'listPrice') return tt('Liste Fiyatı', 'List Price');
    if (field === 'packagePrice') return tt('Paket Fiyatı', 'Package Price');
    return tt('Fiyat', 'Price');
  };

  // ✅ YENİ: Ürün seçildiğinde
  const handleProductSelected = (product: InventoryItemForSelection) => {
    console.log('📦 [BRANCH PRICES] Ürün seçildi:', product);
    
    // InventoryItemForSelection'ı InventoryItem'a dönüştür
    const inventoryItem: InventoryItem = {
      id: product.id,
      name: product.name,
      barcode: product.barcode,
      stockCode: product.stockCode || '',
      unit: product.unit || tt('Adet', 'Piece'),
      salePrice: product.salePrice || 0,
      purchasePrice: 0,
      isActive: true,
      createdAt: new Date().toISOString()
    };
    
    setSelectedProductForBranchPrice(inventoryItem);
    setIsProductSelectionModalOpen(false);
    setIsBranchPriceModalOpen(true);
  };

  // ✅ YENİ: BranchPriceModal başarılı
  const handleBranchPriceSuccess = () => {
    showSnackbar('Şube fiyatı başarıyla eklendi!', 'success');
    loadBranchPrices(); // Listeyi yenile
  };

  // ✅ YENİ: BranchPriceModal kapat
  const handleCloseBranchPriceModal = () => {
    setIsBranchPriceModalOpen(false);
    setSelectedProductForBranchPrice(null);
  };

  // ✅ YENİ: Durum sütununa tıklandığında modal aç
  const handleStatusClick = (price: BranchPriceWithProduct) => {
    console.log('📊 [BRANCH PRICES] Durum tıklandı:', price);
    
    // BranchPriceWithProduct'ı InventoryItem'a dönüştür
    const inventoryItem: InventoryItem = {
      id: price.inventoryItemId,
      name: price.productName,
      barcode: price.barcode,
      stockCode: price.stockCode,
      unit: price.unit || tt('Adet', 'Piece'),
      salePrice: price.salePrice || 0,
      purchasePrice: 0,
      isActive: true,
      useBranchPricing: price.useBranchPricing,
      createdAt: new Date().toISOString()
    };
    
    setSelectedProductForBranchPrice(inventoryItem);
    setIsBranchPriceModalOpen(true);
  };

  // Sıralama — AccountingDataTable dahili olarak yönetiyor

  // Filtreleme (sıralama AccountingDataTable tarafından yönetiliyor)
  const filteredPrices = useMemo(() => {
    let result = [...branchPrices];
    
    // Arama filtresi
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.productName.toLowerCase().includes(term) ||
        item.stockCode.toLowerCase().includes(term) ||
        (item.categoryName && item.categoryName.toLowerCase().includes(term))
      );
    }
    
    return result;
  }, [branchPrices, searchTerm]);

  // Para formatı
  const formatCurrency = (value: number | undefined, currency: string = 'TRY') => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(value);
  };

  // Kolon tanımları
  const priceColumns = useMemo<DataTableColumn<BranchPriceWithProduct>[]>(() => [
    {
      id: 'stockCode',
      label: tt('Stok Kodu', 'Stock Code'),
      field: 'stockCode',
      sortable: true,
      width: '12%',
      render: (row) => (
        <Typography variant="body2" fontWeight="medium">
          {row.stockCode}
        </Typography>
      ),
    },
    {
      id: 'productName',
      label: tt('Ürün Adı', 'Product Name'),
      field: 'productName',
      sortable: true,
      width: '22%',
      render: (row) => (
        <Typography variant="body2">
          {row.productName}
        </Typography>
      ),
    },
    {
      id: 'categoryName',
      label: tt('Kategori', 'Category'),
      sortable: false,
      width: '14%',
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.categoryName || '-'}
        </Typography>
      ),
    },
    {
      id: 'salePrice',
      label: tt('Satış Fiyatı', 'Sale Price'),
      field: 'salePrice',
      sortable: true,
      align: 'right' as const,
      width: '14%',
      cellSx: {
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'primary.lighter',
          '& .edit-icon': { opacity: 1 },
        },
        position: 'relative',
      },
      render: (row) => (
        <Tooltip title={tt('Tıklayarak düzenle', 'Click to edit')} placement="top">
          <Box
            onClick={(e) => { e.stopPropagation(); handlePriceClick(row, 'salePrice'); }}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}
          >
            {/* Siparişler/Teklifler ile aynı fiyat stili: yeşil, bold, sağa hizalı, simge sağda */}
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {(row.salePrice || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
              {row.saleCurrency === 'USD' ? '$' : row.saleCurrency === 'EUR' ? '€' : '₺'}
            </Typography>
            <EditIcon className="edit-icon" sx={{ fontSize: 14, color: 'primary.main', opacity: 0, transition: 'opacity 0.2s' }} />
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'listPrice',
      label: tt('Liste Fiyatı', 'List Price'),
      field: 'listPrice',
      sortable: true,
      align: 'right' as const,
      width: '14%',
      cellSx: {
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'grey.200',
          '& .edit-icon': { opacity: 1 },
        },
        position: 'relative',
      },
      render: (row) => (
        <Tooltip title={tt('Tıklayarak düzenle', 'Click to edit')} placement="top">
          <Box
            onClick={(e) => { e.stopPropagation(); handlePriceClick(row, 'listPrice'); }}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              {formatCurrency(row.listPrice, row.listCurrency || row.saleCurrency)}
            </Typography>
            <EditIcon className="edit-icon" sx={{ fontSize: 14, color: 'text.secondary', opacity: 0, transition: 'opacity 0.2s' }} />
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'packagePrice',
      label: tt('Paket Fiyatı', 'Package Price'),
      field: 'packagePrice',
      sortable: true,
      align: 'right' as const,
      width: '14%',
      cellSx: {
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'secondary.lighter',
          '& .edit-icon': { opacity: 1 },
        },
        position: 'relative',
      },
      render: (row) => (
        <Tooltip title={tt('Tıklayarak düzenle', 'Click to edit')} placement="top">
          <Box
            onClick={(e) => { e.stopPropagation(); handlePriceClick(row, 'packagePrice'); }}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
          >
            <Typography variant="body2" color="secondary.main">
              {formatCurrency(row.packagePrice, row.saleCurrency)}
            </Typography>
            <EditIcon className="edit-icon" sx={{ fontSize: 14, color: 'secondary.main', opacity: 0, transition: 'opacity 0.2s' }} />
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'useBranchPricing',
      label: tt('Durum', 'Status'),
      sortable: false,
      align: 'center' as const,
      width: '10%',
      render: (row) => {
        const statusChip = (
          <Chip
            label={row.useBranchPricing ? tt('Aktif', 'Active') : tt('Pasif', 'Passive')}
            color={row.useBranchPricing ? 'success' : 'default'}
            size="small"
            {...(isManager
              ? {}
              : {
                  onClick: (e: React.MouseEvent) => { e.stopPropagation(); handleStatusClick(row); },
                  sx: {
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2, transform: 'scale(1.05)' },
                    transition: 'all 0.2s',
                  },
                })}
          />
        );

        // Şube yöneticisi durumu yalnızca görür; şube fiyatlandırmasını
        // açıp kapatmak ana yöneticiye aittir.
        return (
          <Tooltip
            title={
              isManager
                ? tt('Şube fiyat durumu yalnızca ana yönetici tarafından değiştirilebilir', 'Branch price status can only be changed by the main administrator')
                : tt('Şube fiyat durumunu değiştirmek için tıklayın', 'Click to change branch price status')
            }
          >
            {statusChip}
          </Tooltip>
        );
      },
    },
  ], [formatCurrency, handlePriceClick, handleStatusClick, isManager, isTr, localeCode]);

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <LocalOfferIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {tt('Şube Fiyatları', 'Branch Prices')}
          </Typography>
        </Box>
      </Box>

      {/* Arama/Filtre ve Butonlar */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Şube Seçimi - ZORUNLU */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={branches}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedBranch}
              disabled={isManager && !!managerBranchId}
              onChange={(_, newValue) => {
                setSelectedBranch(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={isManager ? tt('Şube (Kilitli)', 'Branch (Locked)') : tt('Şube Seçin *', 'Select Branch *')}
                  placeholder=""
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <StoreIcon color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Arama Alanı */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Ürün Ara...', 'Search Product...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedBranch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Boşluk */}
          <Grid item xs={12} md={3} />

          {/* Butonlar */}
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title={tt('Listeyi Yenile', 'Refresh List')}>
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={!selectedBranch || refreshing || loading}
                  color="primary"
                  sx={{
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* ✅ YENİ: Şube fiyatlarını başka şubeye toplu kopyalama.
                Şube yöneticisine kapalı: kopyalama HEDEF şubeyi etkiler, yani
                yöneticinin kendi şubesinin dışına yazar. */}
            {!isManager && (
              <Tooltip
                title={
                  !selectedBranch
                    ? tt('Önce bir şube seçin', 'Select a branch first')
                    : branchPrices.length === 0
                      ? tt('Bu şubede kopyalanacak fiyat yok', 'No prices to copy from this branch')
                      : tt('Bu şubenin tüm fiyatlarını başka bir şubeye toplu kopyala', 'Copy all prices of this branch to another branch in bulk')
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ContentCopyIcon />}
                    disabled={!selectedBranch || branchPrices.length === 0 || loading}
                    onClick={() => setIsCopyModalOpen(true)}
                  >
                    {tt('Başka Şubeye Kopyala', 'Copy To Another Branch')}
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Şube yöneticisi yalnızca MEVCUT fiyatları güncelleyebilir; yeni
                şube fiyatı tanımlamak ana yöneticiye aittir. */}
            {!isManager && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={!selectedBranch}
                onClick={() => setIsProductSelectionModalOpen(true)}
              >
                {tt('Yeni Şube Fiyatı Ekle', 'Add New Branch Price')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Hata Mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Şube Seçilmedi Uyarısı */}
      {!selectedBranch && (
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <StoreIcon 
            sx={{ 
              fontSize: 80, 
              color: 'primary.main',
              mb: 2
            }} 
          />
          
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {tt('Şube Seçin', 'Select Branch')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {tt('Şube fiyatlarını görüntülemek için lütfen yukarıdan bir şube seçin.', 'Please select a branch above to view branch prices.')}
            <br />
            {tt('Seçtiğiniz şube için tanımlanmış tüm ürün fiyatları burada listelenecektir.', 'All product prices defined for the selected branch will be listed here.')}
          </Typography>
        </Box>
      )}

      {/* Ana İçerik - Şube seçiliyse */}
      {selectedBranch && (
        <Box sx={{ mb: 3 }}>
          <AccountingDataTable<BranchPriceWithProduct>
            key={selectedBranch?.id}
            columns={priceColumns}
            rows={filteredPrices}
            getRowId={(p) => p.id}
            loading={loading}
            defaultRowsPerPage={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            defaultSortField="productName"
            defaultSortDirection="asc"
            emptyMessage={searchTerm ? tt('Arama sonucu bulunamadı', 'No search results found') : tt('Bu şube için fiyat tanımlanmamış', 'No prices defined for this branch')}
            emptyIcon={<LocalOfferIcon sx={{ fontSize: 60 }} />}
            ariaLabel={tt('şube fiyatları tablosu', 'branch prices table')}
            maxHeight="calc(100vh - 350px)"
            columnResizeStorageKey="branch-prices"
          />
        </Box>
      )}

      {/* Bilgi Notu */}
      {selectedBranch && !loading && filteredPrices.length > 0 && (
        <Paper sx={{ p: 2, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light' }}>
          <Typography variant="body2" color="info.dark">
            <strong>{tt('Bilgi:', 'Info:')}</strong> {tt('Bu sayfada', 'On this page')} <strong>{selectedBranch.name}</strong> {tt('şubesi için tanımlanmış', 'branch has')} {filteredPrices.length} {tt('adet ürün fiyatı listelenmektedir.', 'defined product prices listed.')}
            {tt('Fiyatları güncellemek için ilgili fiyat hücresine tıklayın.', 'Click the related price cell to update prices.')}
          </Typography>
        </Paper>
      )}

      {/* ✅ Fiyat Güncelleme Modalı */}
      <Dialog
        open={isUpdateModalOpen}
        onClose={handleCloseUpdateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {`${getFieldTitle(selectedField)} ${tt('Güncelle', 'Update')}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Ürün Bilgisi */}
            {selectedPriceItem && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{tt('Ürün:', 'Product:')}</strong> {selectedPriceItem.productName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{tt('Stok Kodu:', 'Stock Code:')}</strong> {selectedPriceItem.stockCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{tt('Şube:', 'Branch:')}</strong> {selectedPriceItem.branchName || selectedBranch?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>{tt('Mevcut', 'Current')} {getFieldTitle(selectedField)}:</strong>{' '}
                  {formatCurrency(
                    selectedField === 'salePrice' ? selectedPriceItem.salePrice :
                    selectedField === 'listPrice' ? selectedPriceItem.listPrice :
                    selectedPriceItem.packagePrice,
                    selectedPriceItem.saleCurrency
                  )}
                </Typography>
              </Box>
            )}
            
            {/* Fiyat Input */}
            <TextField
              autoFocus
              fullWidth
              label={`${tt('Yeni', 'New')} ${getFieldTitle(selectedField)}`}
              type="number"
              value={updateValue}
              onChange={(e) => setUpdateValue(parseFloat(e.target.value) || 0)}
              inputRef={updateInputRef}
              onFocus={(e) => e.target.select()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {selectedPriceItem?.saleCurrency === 'USD' ? '$' : 
                     selectedPriceItem?.saleCurrency === 'EUR' ? '€' : '₺'}
                  </InputAdornment>
                )
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isUpdating) {
                  handleUpdatePrice();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseUpdateModal} 
            color="inherit"
            disabled={isUpdating}
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleUpdatePrice}
            variant="contained"
            color="primary"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : undefined}
          >
            {isUpdating ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Ekleme/kopyalama/durum modalları yalnızca ana yöneticide bağlanır;
          şube yöneticisinde bu modalları açacak bir tetikleyici yoktur. */}
      {!isManager && (
        <>
          {/* ✅ YENİ: Ürün Seçim Modalı */}
          <SingleProductSelectionModal
            open={isProductSelectionModalOpen}
            onClose={() => setIsProductSelectionModalOpen(false)}
            companyId={companyId}
            warehouses={warehouses}
            categories={categories}
            onSelectProduct={handleProductSelected}
          />

          {/* ✅ YENİ: Şube Fiyatı Ekleme Modalı */}
          <BranchPriceModal
            open={isBranchPriceModalOpen}
            onClose={handleCloseBranchPriceModal}
            item={selectedProductForBranchPrice}
            branches={branches}
            companyId={companyId || ''}
            onSuccess={handleBranchPriceSuccess}
            preSelectedBranchId={selectedBranch?.id}
          />

          {/* ✅ YENİ: Şube Fiyatlarını Başka Şubeye Kopyala Modalı */}
          <CopyBranchPricesModal
            open={isCopyModalOpen}
            onClose={() => setIsCopyModalOpen(false)}
            companyId={companyId}
            sourceBranch={selectedBranch}
            sourcePrices={branchPrices}
            branches={branches}
            userId={userDetails?.id || null}
            userName={userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() : null}
            onSuccess={() => {
              showSnackbar(tt('Fiyatlar hedef şubeye kopyalandı.', 'Prices copied to target branch.'), 'success');
            }}
          />
        </>
      )}
    </>
  );
};

export default BranchPrices;
